import React, { FC, ReactNode } from "react";
import {
  createSuspendableResource,
  SuspendableResource,
} from "shared-ts-ui-react";
import { ServerEnvironmentInfo } from "./ServerEnvironment";
import { ServerEnvironmentContext } from "./ServerEnvironmentContext";

export interface ServerEnvironmentProviderProps {
  getServerEnvironmentInfo: () => Promise<ServerEnvironmentInfo>;
  children: (info: ServerEnvironmentInfo) => ReactNode;
}

// The resource is cached at module scope rather than in component state
// (useRef/useMemo) because when this provider suspends, the enclosing
// <Suspense> boundary unmounts it and the component-local cache would be
// lost on every retry, causing the fetch to be re-issued indefinitely.
// A WeakMap keyed by the fetcher function preserves the resource across
// mount/unmount cycles while still allowing multiple distinct providers
// (e.g. in tests) to coexist.
const resourceCache = new WeakMap<
  () => Promise<ServerEnvironmentInfo>,
  SuspendableResource<ServerEnvironmentInfo>
>();

const getOrCreateResource = (
  getServerEnvironmentInfo: () => Promise<ServerEnvironmentInfo>,
): SuspendableResource<ServerEnvironmentInfo> => {
  let resource = resourceCache.get(getServerEnvironmentInfo);
  if (resource === undefined) {
    resource = createSuspendableResource(getServerEnvironmentInfo());
    resourceCache.set(getServerEnvironmentInfo, resource);
  }
  return resource;
};

export const ServerEnvironmentProvider: FC<ServerEnvironmentProviderProps> = (
  props,
) => {
  const { getServerEnvironmentInfo, children } = props;
  const resource = getOrCreateResource(getServerEnvironmentInfo);
  const serverEnvironmentInfo = resource.read();

  return (
    <ServerEnvironmentContext.Provider value={serverEnvironmentInfo}>
      {children(serverEnvironmentInfo)}
    </ServerEnvironmentContext.Provider>
  );
};
